import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Save, MapPin, Phone, Mail, Home, Shield, CheckCircle2, AlertCircle, Bell,
  Plus, Pencil, Trash2, X, Upload, Camera, Inbox, ChevronDown, ChevronLeft, ChevronRight,
} from "lucide-react";
import { myApi, PHOTO_URL } from "../api";
import { useMyAuth } from "../contexts/MyAuthContext";
import { verifyEmail } from "@/shared/emailValidation";

const input =
  "w-full px-4 py-2.5 border border-[#E6E1D6] rounded-[14px] text-sm text-[#0F1829] focus:outline-none focus:ring-2 focus:ring-[#978D78] bg-white transition";

function Tab({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm transition-all whitespace-nowrap ${
        active ? "bg-[#0F1829] text-white shadow-[2px_2px_12px_rgba(15,24,41,0.15)]"
               : "bg-white border border-[#E6E1D6] text-[#4B5563] hover:border-[#978D78]"
      }`}
    >
      <Icon size={15} strokeWidth={1.7} /> {label}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#978D78] font-medium mb-1.5 block">
        {label}
      </label>
      {children}
    </div>
  );
}

function Toast({ msg, onClose }) {
  if (!msg) return null;
  return (
    <div data-testid="my-profile-toast" role="alert" className={`mb-5 px-4 py-2.5 rounded-[14px] text-sm flex items-center gap-2 ${
      msg.type === "success"
        ? "bg-[#F0F5E8] text-[#4A5E2E] border border-[#6F7559]/30"
        : "bg-red-50 text-[#B91C1C] border border-red-200"
    }`}>
      {msg.type === "error" ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />} {msg.text}
      <button onClick={onClose} className="ml-auto text-current opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
}

// Aviso âmbar (informativo, não bloqueia): NIF de empresa já usado noutros
// clientes — evita duplicar o mesmo NIF de empresa em várias pessoas.
function NifWarn({ warn, testid }) {
  if (!warn) return null;
  const nomes = (warn.clientes || []).map((c) => c.nome).slice(0, 3).join(", ");
  return (
    <p data-testid={testid} role="alert"
       className="text-xs mt-1.5 flex items-start gap-1 text-amber-700">
      <AlertCircle size={13} className="mt-0.5 shrink-0" />
      <span>
        Atenção: este NIF de empresa já está em {warn.count} cliente(s)
        {nomes ? ` (ex.: ${nomes})` : ""}. Confirme para não duplicar.
      </span>
    </p>
  );
}

// Verifica no backend se o NIF é de empresa e já está noutros clientes.
async function checkNifUsage(nif) {
  const v = String(nif || "").trim();
  if (v.length !== 9 || !/^[5689]/.test(v)) return null;
  try {
    const u = await myApi.nifUsage(v);
    if (u?.corporate && (u.count || 0) > 0) return u;
  } catch (e) { /* silencioso */ }
  return null;
}

// ─────────────────────────────── Tab: Dados Pessoais + Foto ───────
function TabDados({ refreshMe }) {
  const [profile, setProfile] = useState(null);
  const [paises, setPaises] = useState([]);
  const [moedas, setMoedas] = useState([]);
  const [linguas, setLinguas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [nifWarn, setNifWarn] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [avatarVer, setAvatarVer] = useState(Date.now());
  const fileRef = useRef();
  const { logout } = useMyAuth();
  const [confirmDeact, setConfirmDeact] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    myApi.perfil().then(setProfile).catch(() => setProfile({}));
    myApi.paises().then(setPaises).catch(() => setPaises([]));
    myApi.moedas().then(setMoedas).catch(() => setMoedas([]));
    myApi.linguas().then(setLinguas).catch(() => setLinguas([]));
  }, []);

  const save = async () => {
    if (!profile) return;
    // Email nível 2: bloqueia domínios inexistentes/descartáveis.
    const ev = await verifyEmail(profile.Email || "");
    if (!ev.valid) {
      setMsg({ type: "error",
        text: ev.suggestion ? `${ev.message} Quis dizer ${ev.suggestion}?` : ev.message });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      await myApi.perfilUpdate({
        NomeCompleto: profile.NomeCompleto,
        Email: profile.Email,
        Nif: profile.Nif,
        DataNascimento: profile.DataNascimento ? String(profile.DataNascimento).slice(0, 10) : null,
        Empresa: profile.Empresa,
        Cargo: profile.Cargo,
        ID_Sexo: profile.ID_Sexo,
        ID_Moeda: profile.ID_Moeda,
        ID_Lingua: profile.ID_Lingua,
        NumeroTelemovelDefault: profile.NumeroTelemovelDefault,
      });
      setMsg({ type: "success", text: "Perfil atualizado." });
      refreshMe?.();
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Erro ao guardar." });
    } finally { setSaving(false); }
  };

  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await myApi.uploadPhoto(file);
      setAvatarVer(Date.now());
      setMsg({ type: "success", text: "Foto atualizada." });
      refreshMe?.();
    } catch (ex) {
      setMsg({ type: "error", text: ex.message || "Falha no upload" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    try {
      await myApi.deletePhoto();
      setAvatarVer(Date.now());
      setProfile((p) => ({ ...p, AvatarUrl: null }));
      refreshMe?.();
    } catch (e) { console.error("Avatar refresh failed", e); }
  };

  const deactivate = async () => {
    setDeactivating(true);
    try {
      await myApi.accountDeactivate();
      await logout();
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Erro ao desativar a conta." });
      setDeactivating(false);
      setConfirmDeact(false);
    }
  };

  if (!profile) return <div className="text-sm text-[#978D78]">A carregar...</div>;

  const initials = (profile.NomeCompleto || "?").charAt(0).toUpperCase();
  const hasPhoto = !!profile.AvatarUrl;

  return (
    <div data-testid="my-profile-dados" className="bg-white rounded-[20px] border border-[#E6E1D6] p-6 lg:p-8">
      <Toast msg={msg} onClose={() => setMsg(null)} />

      {/* Photo upload */}
      <div className="flex items-center gap-6 mb-8 pb-6 border-b border-[#F5F2EB]">
        <div className="relative">
          {hasPhoto ? (
            <img
              src={`${PHOTO_URL}?v=${avatarVer}`}
              alt={profile.NomeCompleto}
              data-testid="my-profile-avatar"
              className="w-24 h-24 rounded-full object-cover border-2 border-[#E6E1D6]"
            />
          ) : (
            <div
              data-testid="my-profile-avatar-placeholder"
              className="w-24 h-24 rounded-full bg-gradient-to-br from-[#0F1829] to-[#978D78] grid place-items-center text-white text-3xl"
              style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
            >
              {initials}
            </div>
          )}
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="my-profile-photo-upload-btn"
            className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#0F1829] text-white grid place-items-center shadow-lg hover:bg-[#1a2542] transition"
            title="Alterar foto"
          >
            <Camera size={15} />
          </button>
        </div>
        <div>
          <div className="text-sm text-[#0F1829] font-medium mb-1">Foto de perfil</div>
          <div className="text-xs text-[#6B7280] mb-3">JPG/PNG, máx 2MB</div>
          <div className="flex gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="my-profile-photo-choose"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[#F5F2EB] hover:bg-[#E6E1D6] text-[#0F1829]"
            >
              <Upload size={13} /> {uploading ? "A enviar..." : "Enviar foto"}
            </button>
            {hasPhoto && (
              <button
                onClick={removePhoto}
                data-testid="my-profile-photo-remove"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-red-50 hover:bg-red-100 text-[#B91C1C]"
              >
                <Trash2 size={13} /> Remover
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPhotoPick} className="hidden" data-testid="my-profile-photo-input" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Nome Completo">
          <input value={profile.NomeCompleto || ""} onChange={(e) => setProfile({ ...profile, NomeCompleto: e.target.value })} className={input} data-testid="my-profile-nome" />
        </Field>
        <Field label="Email">
          <input type="email" value={profile.Email || ""} onChange={(e) => setProfile({ ...profile, Email: e.target.value })} className={input} data-testid="my-profile-email" />
        </Field>
        <Field label="NIF">
          <input value={profile.Nif || ""} onChange={(e) => { setProfile({ ...profile, Nif: e.target.value }); setNifWarn(null); }} onBlur={async (e) => setNifWarn(await checkNifUsage(e.target.value))} className={input} data-testid="my-profile-nif" />
          <NifWarn warn={nifWarn} testid="my-profile-nif-warn" />
        </Field>
        <Field label="Data de Nascimento">
          <input type="date" value={profile.DataNascimento ? String(profile.DataNascimento).slice(0, 10) : ""} onChange={(e) => setProfile({ ...profile, DataNascimento: e.target.value })} className={input} data-testid="my-profile-datanasc" />
        </Field>
        <Field label="Sexo">
          <select value={profile.ID_Sexo || ""} onChange={(e) => setProfile({ ...profile, ID_Sexo: e.target.value })} className={input} data-testid="my-profile-sexo">
            <option value="">—</option>
            <option value="M">Masculino</option>
            <option value="F">Feminino</option>
          </select>
        </Field>
        <Field label="Telemóvel">
          <input value={profile.NumeroTelemovelDefault || ""} onChange={(e) => setProfile({ ...profile, NumeroTelemovelDefault: e.target.value })} className={input} data-testid="my-profile-phone" />
        </Field>
        <Field label="Moeda">
          <select value={profile.ID_Moeda || ""} onChange={(e) => setProfile({ ...profile, ID_Moeda: e.target.value })} className={input} data-testid="my-profile-moeda">
            <option value="">—</option>
            {moedas.map((m) => <option key={m.ID_Moeda} value={m.ID_Moeda}>{m.Descricao} ({m.ID_Moeda})</option>)}
          </select>
        </Field>
        <Field label="Idioma">
          <select value={profile.ID_Lingua || ""} onChange={(e) => setProfile({ ...profile, ID_Lingua: e.target.value })} className={input} data-testid="my-profile-lingua">
            <option value="">—</option>
            {linguas.map((l) => <option key={l.ID_Lingua} value={l.ID_Lingua}>{l.Descricao}</option>)}
          </select>
        </Field>
        <Field label="Empresa">
          <input value={profile.Empresa || ""} onChange={(e) => setProfile({ ...profile, Empresa: e.target.value })} className={input} />
        </Field>
        <Field label="Cargo">
          <input value={profile.Cargo || ""} onChange={(e) => setProfile({ ...profile, Cargo: e.target.value })} className={input} />
        </Field>
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="my-profile-save-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F1829] text-white text-sm rounded-full hover:bg-[#1a2542] active:scale-[0.98] disabled:opacity-50 shadow-[2px_2px_12px_rgba(15,24,41,0.15)]">
          <Save size={16} /> {saving ? "A guardar..." : "Guardar alterações"}
        </button>
      </div>

      {/* Zona de conta: desativar/reativar */}
      <div className="mt-8 pt-6 border-t border-[#F5F2EB]" data-testid="my-profile-account-zone">
        <div className="text-sm font-medium text-[#B91C1C] mb-1">Desativar conta</div>
        <p className="text-xs text-[#6B7280] mb-3 max-w-xl">
          Ao desativar, a sua conta fica inativa e a sua sessão termina.
        </p>
        <button
          onClick={() => setConfirmDeact(true)}
          data-testid="my-profile-deactivate-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-red-50 hover:bg-red-100 text-[#B91C1C] border border-red-200 transition"
        >
          <Trash2 size={14} /> Desativar a minha conta
        </button>
      </div>

      {confirmDeact && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="my-profile-deactivate-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold text-[#0F1829] mb-2">Desativar a conta?</h3>
            <p className="text-sm text-[#6B7280] mb-5">
              A sua conta ficará inativa e a sessão será terminada.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDeact(false)}
                disabled={deactivating}
                data-testid="my-profile-deactivate-cancel"
                className="px-4 py-2 rounded-full text-sm bg-[#F5F2EB] hover:bg-[#E6E1D6] text-[#0F1829] disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={deactivate}
                disabled={deactivating}
                data-testid="my-profile-deactivate-confirm"
                className="px-4 py-2 rounded-full text-sm bg-[#B91C1C] hover:bg-[#991818] text-white disabled:opacity-50"
              >
                {deactivating ? "A desativar..." : "Sim, desativar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────── Tab: Moradas CRUD ────────────────
function MoradaDialog({ tipo, pais, onClose, onSave, initial }) {
  const [f, setF] = useState(initial || {
    ID_TipoMorada: tipo[0]?.ID_TipoMorada || "R",
    Nome: "", Nif: "", Morada: "", Localidade: "",
    CodigoPostal: "", ID_Pais: "PRT", Principal: "N",
  });
  const [erro, setErro] = useState("");
  const [campoErro, setCampoErro] = useState("");
  const [nifWarn, setNifWarn] = useState(null);

  // Erro visível: mensagem destacada + campo realçado + foco no campo em falta
  // (antes o botão Guardar não fazia nada e o cliente não sabia porquê).
  const falhar = (msg, campo) => {
    setErro(msg); setCampoErro(campo || "");
    setTimeout(() => {
      const el = campo ? document.getElementById(`morada-f-${campo}`) : null;
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus({ preventScroll: true }); }
    }, 60);
  };

  const save = async () => {
    setErro(""); setCampoErro("");
    if (!(f.Morada || "").trim()) return falhar("Indique a morada (rua, nº, andar).", "Morada");
    if (!(f.CodigoPostal || "").trim()) return falhar("Indique o código postal.", "CodigoPostal");
    if (!(f.Localidade || "").trim()) return falhar("Indique a localidade.", "Localidade");
    if (String(f.ID_TipoMorada) === "F" && !(f.Nif || "").trim()) {
      return falhar("Para morada de facturação indique o NIF.", "Nif");
    }
    try {
      await onSave(f);
    } catch (e) {
      falhar(e?.message || "Não foi possível guardar a morada.");
    }
  };

  const cls = (campo) => `${input}${campoErro === campo ? " !border-rose-500 !bg-rose-50" : ""}`;
  return (
    <div data-testid="my-morada-dialog" className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-[24px] w-full max-w-xl p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {initial?.ID_ClienteMorada ? "Editar Morada" : "Nova Morada"}
          </h3>
          <button onClick={onClose} className="p-1 text-[#978D78] hover:text-[#0F1829]"><X size={18} /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Tipo">
            <select value={f.ID_TipoMorada} onChange={(e) => setF({ ...f, ID_TipoMorada: e.target.value })} className={input} data-testid="morada-tipo">
              {tipo.map((t) => <option key={t.ID_TipoMorada} value={t.ID_TipoMorada}>{t.Descricao}</option>)}
            </select>
          </Field>
          <Field label="Nome">
            <input value={f.Nome || ""} onChange={(e) => setF({ ...f, Nome: e.target.value })} className={input} data-testid="morada-nome" />
          </Field>
          <Field label="NIF (para facturação)">
            <input id="morada-f-Nif" value={f.Nif || ""} onChange={(e) => { setF({ ...f, Nif: e.target.value }); setNifWarn(null); }} onBlur={async (e) => setNifWarn(await checkNifUsage(e.target.value))} className={cls("Nif")} data-testid="morada-nif" />
            <NifWarn warn={nifWarn} testid="morada-nif-warn" />
          </Field>
          <Field label="País">
            <select value={f.ID_Pais} onChange={(e) => setF({ ...f, ID_Pais: e.target.value })} className={input} data-testid="morada-pais">
              {pais.map((p) => <option key={p.ID_Pais} value={p.ID_Pais}>{p.Descricao}</option>)}
            </select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Morada">
              <input id="morada-f-Morada" value={f.Morada || ""} onChange={(e) => setF({ ...f, Morada: e.target.value })} className={cls("Morada")} data-testid="morada-morada" />
            </Field>
          </div>
          <Field label="Código Postal">
            <input id="morada-f-CodigoPostal" value={f.CodigoPostal || ""} onChange={(e) => setF({ ...f, CodigoPostal: e.target.value })} className={cls("CodigoPostal")} data-testid="morada-cp" />
          </Field>
          <Field label="Localidade">
            <input id="morada-f-Localidade" value={f.Localidade || ""} onChange={(e) => setF({ ...f, Localidade: e.target.value })} className={cls("Localidade")} data-testid="morada-localidade" />
          </Field>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm text-[#4B5563]">
          <input type="checkbox" checked={f.Principal === "S"} onChange={(e) => setF({ ...f, Principal: e.target.checked ? "S" : "N" })} data-testid="morada-principal" />
          Definir como principal
        </label>
        {erro ? (
          <div data-testid="morada-error" role="alert"
               className="mt-4 flex items-start gap-2 rounded-xl border-2 border-rose-500 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full bg-rose-600 text-[10px] font-black text-white">!</span>
            <span>{erro}</span>
          </div>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-full border border-[#E6E1D6] text-[#4B5563] hover:bg-[#F5F2EB]">Cancelar</button>
          <button onClick={save} data-testid="morada-save" className="px-5 py-2 text-sm rounded-full bg-[#0F1829] text-white hover:bg-[#1a2542]">Guardar</button>
        </div>
      </div>
    </div>
  );
}

function TabMoradas() {
  const [moradas, setMoradas] = useState([]);
  const [tipo, setTipo] = useState([]);
  const [pais, setPais] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = () => myApi.moradas().then(setMoradas).catch(() => setMoradas([]));
  useEffect(() => {
    load();
    myApi.moradaTipos().then(setTipo).catch(() => setTipo([]));
    myApi.paises().then(setPais).catch(() => setPais([]));
  }, []);

  const onSave = async (f) => {
    try {
      if (f.ID_ClienteMorada) await myApi.moradaUpdate(f.ID_ClienteMorada, f);
      else await myApi.moradaCreate(f);
      setEditing(null); setMsg({ type: "success", text: "Morada guardada." });
      load();
    } catch (e) {
      setMsg({ type: "error", text: e.message });
      throw e;  // o diálogo mostra o erro junto ao botão Guardar
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminar morada?")) return;
    try { await myApi.moradaDelete(id); load(); setMsg({ type: "success", text: "Morada eliminada." }); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
  };

  return (
    <div data-testid="my-profile-moradas">
      <Toast msg={msg} onClose={() => setMsg(null)} />
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setEditing({})}
          data-testid="morada-new-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#0F1829] text-white rounded-full hover:bg-[#1a2542]"
        >
          <Plus size={15} /> Nova morada
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {moradas.length === 0 && (
          <div className="col-span-full bg-white rounded-[20px] p-8 text-center text-sm text-[#6B7280] border border-[#E6E1D6]">
            Nenhuma morada registada.
          </div>
        )}
        {moradas.map((m) => (
          <div key={m.ID_ClienteMorada} data-testid={`my-morada-${m.ID_ClienteMorada}`} className="bg-white rounded-[20px] border border-[#E6E1D6] p-5 relative group">
            {m.Principal === "S" && (
              <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#0F1829] text-white">
                Principal
              </span>
            )}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F5F2EB] text-[#978D78] grid place-items-center">
                <MapPin size={18} strokeWidth={1.7} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#978D78] mb-1">
                  {m.TipoMorada || m.ID_TipoMorada}
                </div>
                <div className="text-sm text-[#0F1829] mb-0.5">{m.Nome || "—"}</div>
                <div className="text-sm text-[#4B5563]">{m.Morada}</div>
                <div className="text-sm text-[#4B5563]">{m.CodigoPostal} {m.Localidade} {m.Pais ? `· ${m.Pais}` : ""}</div>
                {m.Nif && <div className="text-xs text-[#978D78] mt-1">NIF: {m.Nif}</div>}
              </div>
            </div>
            <div className="mt-4 flex gap-2 opacity-70 group-hover:opacity-100 transition">
              <button onClick={() => setEditing(m)} data-testid={`morada-edit-${m.ID_ClienteMorada}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#F5F2EB] text-[#0F1829] hover:bg-[#E6E1D6]">
                <Pencil size={12} /> Editar
              </button>
              <button onClick={() => onDelete(m.ID_ClienteMorada)} data-testid={`morada-del-${m.ID_ClienteMorada}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-50 text-[#B91C1C] hover:bg-red-100">
                <Trash2 size={12} /> Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>
      {editing !== null && (
        <MoradaDialog tipo={tipo} pais={pais} onClose={() => setEditing(null)} onSave={onSave} initial={editing.ID_ClienteMorada ? editing : null} />
      )}
    </div>
  );
}

// ─────────────────────────────── Tab: Contactos CRUD ──────────────
function ContactoDialog({ tipo, initial, onClose, onSave }) {
  const [f, setF] = useState(initial || {
    ID_TipoContacto: tipo[0]?.ID_TipoContacto || 2,
    Contacto: "", Principal: "N",
  });
  const [erro, setErro] = useState("");

  // Se o contacto é um email, valida domínio antes de gravar.
  const guardar = async () => {
    setErro("");
    const val = (f.Contacto || "").trim();
    if (!val) { setErro("Indique o contacto."); return; }
    if (val.includes("@")) {
      const ev = await verifyEmail(val);
      if (!ev.valid) {
        setErro(ev.suggestion ? `${ev.message} Quis dizer ${ev.suggestion}?` : ev.message);
        return;
      }
    }
    onSave(f);
  };
  return (
    <div data-testid="my-contacto-dialog" className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-[24px] w-full max-w-md p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            {initial?.ID_ClienteContacto ? "Editar Contacto" : "Novo Contacto"}
          </h3>
          <button onClick={onClose} className="p-1 text-[#978D78]"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <Field label="Tipo">
            <select value={f.ID_TipoContacto} onChange={(e) => setF({ ...f, ID_TipoContacto: Number(e.target.value) })} className={input} data-testid="contacto-tipo">
              {tipo.map((t) => <option key={t.ID_TipoContacto} value={t.ID_TipoContacto}>{t.Descricao}</option>)}
            </select>
          </Field>
          <Field label="Contacto">
            <input value={f.Contacto || ""} onChange={(e) => setF({ ...f, Contacto: e.target.value })} className={input} data-testid="contacto-valor" />
          </Field>
          <label className="flex items-center gap-2 text-sm text-[#4B5563]">
            <input type="checkbox" checked={f.Principal === "S"} onChange={(e) => setF({ ...f, Principal: e.target.checked ? "S" : "N" })} data-testid="contacto-principal" />
            Definir como principal
          </label>
          {erro && (
            <div data-testid="contacto-error" role="alert"
              className="p-2.5 rounded-lg bg-red-50 border-2 border-[#DC2626] text-[#DC2626] text-xs font-bold flex items-start gap-2">
              <span className="w-4 h-4 rounded-full bg-[#DC2626] text-white text-[10px] grid place-items-center shrink-0">!</span>
              <span>{erro}</span>
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-full border border-[#E6E1D6]">Cancelar</button>
          <button onClick={guardar} data-testid="contacto-save" className="px-5 py-2 text-sm rounded-full bg-[#0F1829] text-white">Guardar</button>
        </div>
      </div>
    </div>
  );
}

function TabContactos() {
  const [contactos, setContactos] = useState([]);
  const [tipo, setTipo] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = () => myApi.contactos().then(setContactos).catch(() => setContactos([]));
  useEffect(() => {
    load();
    myApi.contactoTipos().then(setTipo).catch(() => setTipo([]));
  }, []);

  const onSave = async (f) => {
    try {
      if (f.ID_ClienteContacto) await myApi.contactoUpdate(f.ID_ClienteContacto, f);
      else await myApi.contactoCreate(f);
      setEditing(null); setMsg({ type: "success", text: "Contacto guardado." });
      load();
    } catch (e) { setMsg({ type: "error", text: e.message }); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminar contacto?")) return;
    try { await myApi.contactoDelete(id); load(); setMsg({ type: "success", text: "Contacto eliminado." }); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
  };

  return (
    <div data-testid="my-profile-contactos">
      <Toast msg={msg} onClose={() => setMsg(null)} />
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({})} data-testid="contacto-new-btn" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#0F1829] text-white rounded-full hover:bg-[#1a2542]">
          <Plus size={15} /> Novo contacto
        </button>
      </div>
      <div className="bg-white rounded-[20px] border border-[#E6E1D6] overflow-hidden">
        {contactos.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6B7280]">Sem contactos registados.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#F5F2EB]">
              <tr>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Tipo</th>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Contacto</th>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#978D78]">Principal</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F2EB]">
              {contactos.map((c) => {
                const I = String(c.TipoContacto || "").toLowerCase().includes("email") ? Mail : Phone;
                return (
                  <tr key={c.ID_ClienteContacto} data-testid={`my-contacto-${c.ID_ClienteContacto}`} className="hover:bg-[#FAF8F3]">
                    <td className="px-5 py-3 text-sm text-[#0F1829]">
                      <span className="inline-flex items-center gap-2">
                        <I size={14} strokeWidth={1.7} className="text-[#978D78]" /> {c.TipoContacto || c.ID_TipoContacto}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-[#4B5563]">{c.Contacto}</td>
                    <td className="px-5 py-3">
                      {c.Principal === "S" ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#0F1829] text-white">Sim</span>
                      ) : <span className="text-[#978D78] text-xs">—</span>}
                    </td>
                    <td className="px-5 py-3 text-right space-x-1">
                      <button onClick={() => setEditing(c)} data-testid={`contacto-edit-${c.ID_ClienteContacto}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#F5F2EB] text-[#0F1829] hover:bg-[#E6E1D6]">
                        <Pencil size={11} />
                      </button>
                      <button onClick={() => onDelete(c.ID_ClienteContacto)} data-testid={`contacto-del-${c.ID_ClienteContacto}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-50 text-[#B91C1C] hover:bg-red-100">
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {editing !== null && (
        <ContactoDialog tipo={tipo} initial={editing.ID_ClienteContacto ? editing : null} onClose={() => setEditing(null)} onSave={onSave} />
      )}
    </div>
  );
}

// ─────────────────────────────── Tab: Password ────────────────────
function TabPassword() {
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNew2, setPwNew2] = useState("");
  const [pwMsg, setPwMsg] = useState(null);

  const savePassword = async () => {
    setPwMsg(null);
    if (pwNew.length < 6) return setPwMsg({ type: "error", text: "Mínimo 6 caracteres." });
    if (pwNew !== pwNew2) return setPwMsg({ type: "error", text: "As palavras-passe não coincidem." });
    try {
      await myApi.changePassword(pwOld, pwNew);
      setPwMsg({ type: "success", text: "Palavra-passe alterada." });
      setPwOld(""); setPwNew(""); setPwNew2("");
    } catch (e) {
      setPwMsg({ type: "error", text: e.message || "Erro ao alterar." });
    }
  };

  return (
    <div data-testid="my-profile-password" className="bg-white rounded-[20px] border border-[#E6E1D6] p-6 lg:p-8 max-w-xl">
      <Toast msg={pwMsg} onClose={() => setPwMsg(null)} />
      <div className="space-y-5">
        <Field label="Palavra-passe atual">
          <input type="password" value={pwOld} onChange={(e) => setPwOld(e.target.value)} className={input} data-testid="my-pw-old" />
        </Field>
        <Field label="Nova palavra-passe">
          <input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} className={input} data-testid="my-pw-new" />
        </Field>
        <Field label="Confirmar nova palavra-passe">
          <input type="password" value={pwNew2} onChange={(e) => setPwNew2(e.target.value)} className={input} data-testid="my-pw-new2" />
        </Field>
      </div>
      <div className="mt-6 flex justify-end">
        <button onClick={savePassword} data-testid="my-pw-save-btn" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F1829] text-white text-sm rounded-full hover:bg-[#1a2542]">
          <Shield size={16} /> Alterar palavra-passe
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────── Tab: Notificações Email ──────────
function TabAlertas() {
  const [list, setList] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = () => myApi.alertas().then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    try {
      await myApi.alertaToggle(t.ID_TipoAlertaSubscrito, !t.Subscrito);
      setMsg({ type: "success", text: t.Subscrito ? "Subscrição cancelada." : "Subscrição activada." });
      load();
    } catch (e) { setMsg({ type: "error", text: e.message }); }
  };

  if (!list) return <div className="text-sm text-[#978D78]">A carregar...</div>;

  return (
    <div data-testid="my-profile-alertas">
      <Toast msg={msg} onClose={() => setMsg(null)} />
      <div className="bg-white rounded-[20px] border border-[#E6E1D6] divide-y divide-[#F5F2EB]">
        {list.map((a) => (
          <div key={a.ID_TipoAlertaSubscrito} data-testid={`my-alerta-${a.ID_TipoAlertaSubscrito}`} className="p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#F5F2EB] text-[#978D78] grid place-items-center shrink-0">
              <Bell size={18} strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#0F1829] font-medium mb-1">{a.Descricao}</div>
              {a.Texto && <div className="text-xs text-[#6B7280] leading-relaxed">{a.Texto}</div>}
            </div>
            <button
              onClick={() => toggle(a)}
              data-testid={`my-alerta-toggle-${a.ID_TipoAlertaSubscrito}`}
              className={`relative w-12 h-6 rounded-full transition ${a.Subscrito ? "bg-[#0F1829]" : "bg-[#E6E1D6]"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${a.Subscrito ? "translate-x-6" : ""}`} />
            </button>
          </div>
        ))}
        {list.length === 0 && <div className="p-8 text-center text-sm text-[#6B7280]">Sem tipos de alerta disponíveis.</div>}
      </div>
    </div>
  );
}

// ─────────────────────────────── Tab: Emails Recebidos ────────────
function TabEmails() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setData(null);
    myApi.perfilEmails(page, 10).then(setData).catch(() => setData({ items: [], total: 0, total_pages: 1 }));
  }, [page]);

  if (!data) return <div className="text-sm text-[#978D78]">A carregar...</div>;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" }) +
      " " + d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div data-testid="my-profile-emails">
      <div className="bg-white rounded-[20px] border border-[#E6E1D6] divide-y divide-[#F5F2EB]">
        {data.items.length === 0 && (
          <div className="p-8 text-center text-sm text-[#6B7280]">Ainda não lhe foi enviado nenhum email.</div>
        )}
        {data.items.map((e) => {
          const isOpen = open === e.ID_EmailEnviado;
          return (
            <div key={e.ID_EmailEnviado} data-testid={`my-email-${e.ID_EmailEnviado}`}>
              <button
                onClick={() => setOpen(isOpen ? null : e.ID_EmailEnviado)}
                data-testid={`my-email-toggle-${e.ID_EmailEnviado}`}
                className="w-full p-4 sm:p-5 flex items-start gap-3 sm:gap-4 text-left hover:bg-[#FAF8F3] transition"
              >
                <div className="w-10 h-10 rounded-full bg-[#F5F2EB] text-[#978D78] grid place-items-center shrink-0">
                  <Mail size={18} strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0F1829] font-medium mb-0.5 truncate">
                    {e.Assunto || "(sem assunto)"}
                  </div>
                  <div className="text-xs text-[#978D78]">
                    {fmtDate(e.DataEnvio)}{e.EmailDestino ? ` · para ${e.EmailDestino}` : ""}
                  </div>
                </div>
                <ChevronDown size={16} className={`shrink-0 mt-2 text-[#978D78] transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-4 sm:px-5 pb-5 pl-[68px] sm:pl-[76px]" data-testid={`my-email-body-${e.ID_EmailEnviado}`}>
                  {e.Mensagem ? (
                    <div className="text-sm text-[#4B5563] leading-relaxed border border-[#F5F2EB] rounded-[14px] p-4 bg-[#FBFAF7] overflow-x-auto"
                      dangerouslySetInnerHTML={{ __html: e.Mensagem }} />
                  ) : (
                    <p className="text-xs text-[#978D78] italic">
                      Email automático — o conteúdo completo não ficou arquivado.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {data.total_pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="my-emails-prev"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-[#E6E1D6] text-[#4B5563] hover:bg-[#F5F2EB] disabled:opacity-40">
            <ChevronLeft size={13} /> Anterior
          </button>
          <span className="text-xs text-[#978D78]">Página {data.page} de {data.total_pages} ({data.total} emails)</span>
          <button disabled={page >= data.total_pages} onClick={() => setPage(page + 1)} data-testid="my-emails-next"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-[#E6E1D6] text-[#4B5563] hover:bg-[#F5F2EB] disabled:opacity-40">
            Seguinte <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────── Page ─────────────────────────────
export default function MyProfile() {
  const [searchParams] = useSearchParams();
  const { refresh } = useMyAuth();
  const VALID_TABS = ["dados", "moradas", "contactos", "alertas", "emails", "password"];
  const initialTab = VALID_TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "dados";
  const [tab, setTab] = useState(initialTab);

  return (
    <div data-testid="my-profile-page" className="space-y-6">
      <div>
        <h1 className="text-4xl text-[#0F1829] mb-1" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
          Informações Pessoais
        </h1>
        <p className="text-sm text-[#6B7280]">Gerir os seus dados, moradas, contactos, palavra-passe e notificações.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Tab active={tab === "dados"} onClick={() => setTab("dados")} icon={Home} label="Dados Pessoais" testid="my-tab-dados" />
        <Tab active={tab === "moradas"} onClick={() => setTab("moradas")} icon={MapPin} label="Moradas" testid="my-tab-moradas" />
        <Tab active={tab === "contactos"} onClick={() => setTab("contactos")} icon={Phone} label="Contactos" testid="my-tab-contactos" />
        <Tab active={tab === "alertas"} onClick={() => setTab("alertas")} icon={Bell} label="Notificações Email" testid="my-tab-alertas" />
        <Tab active={tab === "emails"} onClick={() => setTab("emails")} icon={Inbox} label="Emails Recebidos" testid="my-tab-emails" />
        <Tab active={tab === "password"} onClick={() => setTab("password")} icon={Shield} label="Palavra-Passe" testid="my-tab-password" />
      </div>

      {tab === "dados" && <TabDados refreshMe={refresh} />}
      {tab === "moradas" && <TabMoradas />}
      {tab === "contactos" && <TabContactos />}
      {tab === "alertas" && <TabAlertas />}
      {tab === "emails" && <TabEmails />}
      {tab === "password" && <TabPassword />}
    </div>
  );
}
